import { useEffect, useId, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  CalendarBlank,
  CaretLeft,
  CaretRight,
  CircleNotch,
  Clock,
  MagnifyingGlass,
  User,
  Stethoscope,
  PaperPlaneTilt,
  LockSimple,
  Wallet,
  X,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { apiGet } from "@/lib/api-error";
import { formatCurrency, formatDateWithOptionsBs } from "@/lib/format";
import { BookingStepper } from "@/components/booking/BookingStepper";
import { CalendarGrid } from "@/components/booking/CalendarGrid";
import { groupSlotsByPartOfDay, type PartOfDay } from "@/lib/slot-groups";

interface Service { id: string; name: string; price: number; durationMinutes: number; modality: "online" | "in_person" | "both" }
interface ClientRow { id: string; name: string; email: string }
interface CompanyCreditInfo {
  companyId: string;
  companyName: string;
  balance: number;
  discountPercent: number;
  billingModel: "credit" | "invoice";
  cofundingPercent: number;
  /** Exact amount POST /api/therapist/sessions charges the company for the selected service (server-computed). */
  chargeKm?: number | null;
}

export interface ManualBookingBody {
  clientId: string;
  serviceId: string;
  date: string;
  time: string;
  paymentMode: "company_credit" | "send_link";
  modality?: "online" | "in_person";
}

interface AddSessionDialogProps {
  therapistId: string;
  onClose: () => void;
  onConfirm: (body: ManualBookingBody) => void;
  isPending: boolean;
}

type PaymentMode = "company_credit" | "send_link";
type WizardStep = "client" | "service" | "appointment" | "payment" | "confirm";

const WIZARD_STEPS: { key: WizardStep; label: string }[] = [
  { key: "client", label: "Klijent" },
  { key: "service", label: "Usluga" },
  { key: "appointment", label: "Termin" },
  { key: "payment", label: "Naplata" },
  { key: "confirm", label: "Potvrda" },
];

const PAYMENT_OPTIONS: {
  value: PaymentMode;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string; weight?: "regular" | "fill" | "duotone" }>;
}[] = [
  {
    value: "send_link",
    title: "Pošalji link za plaćanje",
    description: "Klijent dobije email s podacima za uplatu.",
    icon: PaperPlaneTilt,
  },
];

const PART_LABEL: Record<PartOfDay, string> = {
  morning: "booking.partMorning",
  afternoon: "booking.partAfternoon",
  evening: "booking.partEvening",
};

function modalityLabel(
  modality: Service["modality"] | "online" | "in_person" | undefined,
  t: (key: string) => string,
) {
  if (modality === "in_person") return t("booking.modalityInPerson");
  if (modality === "both") return `${t("booking.modalityOnline")} · ${t("booking.modalityInPerson")}`;
  return t("booking.modalityOnline");
}

function paymentLabel(mode: PaymentMode) {
  if (mode === "company_credit") return "Preko firme";
  return "Link za plaćanje";
}

function formatDateLabel(date: string) {
  if (!date) return "";
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return date;
  return formatDateWithOptionsBs(new Date(year, month - 1, day), {
    weekday: "short",
    day: "numeric",
    month: "long",
  });
}

function toYmd(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function SummaryRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border py-2.5 last:border-b-0">
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className="text-right text-[13.5px] font-medium text-foreground">{value || "—"}</span>
    </div>
  );
}

function CompactTimeSlots({
  therapistId,
  serviceId,
  dateStr,
  selectedTime,
  enabled,
  onSelectTime,
}: {
  therapistId: string;
  /** Selected service: the grid only offers starts where its full duration fits. */
  serviceId: string | null;
  dateStr: string;
  selectedTime: string | null;
  enabled: boolean;
  onSelectTime: (time: string) => void;
}) {
  const { t } = useTranslation();
  const { data: slotsData, isLoading } = useQuery({
    // "withBooked" namespaces this from the available-only grid used by
    // RescheduleDialog — same key otherwise, different payload shape.
    queryKey: ["available-slots", therapistId, dateStr, "withBooked", serviceId],
    queryFn: async () => {
      const params = new URLSearchParams({ date: dateStr, includeBooked: "1" });
      if (serviceId) params.set("serviceId", serviceId);
      const res = await fetch(`/api/therapists/${therapistId}/available-slots?${params}`);
      if (!res.ok) return { slots: [] };
      return res.json() as Promise<{ slots: Array<{ time: string; endTime: string; booked?: boolean }> }>;
    },
    enabled,
  });

  const groups = groupSlotsByPartOfDay(slotsData?.slots ?? []);

  if (isLoading) {
    return (
      <div className="flex min-h-[180px] items-center justify-center">
        <CircleNotch className="size-5 animate-spin text-primary/50" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <p className="grid min-h-[180px] place-items-center rounded-2xl bg-muted/20 px-4 text-center text-sm text-muted-foreground">
        {t("booking.noSlots")}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.part}>
          <p className="mb-2 text-[12px] font-semibold uppercase text-muted-foreground/70">
            {t(PART_LABEL[group.part])}
          </p>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(78px,1fr))] gap-2">
            {group.slots.map((slot) => {
              const active = selectedTime === slot.time;
              const booked = slot.booked === true;
              return (
                <button
                  key={slot.time}
                  type="button"
                  disabled={booked}
                  aria-label={booked ? `${slot.time} — ${t("booking.slotBooked")}` : slot.time}
                  onClick={() => {
                    if (!booked) onSelectTime(slot.time);
                  }}
                  className={cn(
                    "inline-flex h-10 min-w-0 items-center justify-center gap-1 rounded-xl border px-3 text-sm font-semibold leading-none transition-colors",
                    booked
                      ? "cursor-not-allowed border-border/60 bg-muted/30 text-muted-foreground/55 line-through"
                      : active
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-white text-foreground hover:border-primary hover:bg-primary/5",
                  )}
                >
                  {booked && <LockSimple className="size-3.5 shrink-0" weight="fill" />}
                  <span className="whitespace-nowrap">{slot.time}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export function AddSessionDialog({ therapistId, onClose, onConfirm, isPending }: AddSessionDialogProps) {
  const { t } = useTranslation();
  const ids = useId();
  const [step, setStep] = useState<WizardStep>("client");
  const [clientId, setClientId] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState<ClientRow | null>(null);
  const [serviceId, setServiceId] = useState("");
  const [modality, setModality] = useState<"online" | "in_person">("online");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [paymentMode, setPaymentMode] = useState<PaymentMode>("send_link");
  const [paymentTouched, setPaymentTouched] = useState(false);

  const { data: servicesData } = useQuery({
    queryKey: ["services", "therapist", therapistId],
    queryFn: () => apiGet<{ services: Service[] }>(`/api/services?therapistId=${therapistId}`, "Greška"),
    enabled: !!therapistId,
  });
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(clientQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [clientQuery]);

  // Re-evaluate auto-selection when the client changes.
  useEffect(() => {
    setPaymentTouched(false);
  }, [clientId]);

  const { data: clientsData, isFetching: clientsFetching } = useQuery({
    queryKey: ["therapist", "clients", debouncedQuery],
    queryFn: () => apiGet<{ clients: ClientRow[] }>(`/api/therapist/clients?q=${encodeURIComponent(debouncedQuery)}`, "Greška"),
    enabled: !clientId,
  });

  // With serviceId the server also returns chargeKm: the exact company charge
  // (business default country + companyFundingShares), the same number the
  // booking endpoint charges. The dialog never recomputes it.
  const { data: companyCredit = null } = useQuery({
    queryKey: ["therapist", "client-company-credit", clientId, serviceId],
    queryFn: async () => {
      const qs = serviceId ? `?serviceId=${encodeURIComponent(serviceId)}` : "";
      const res = await fetch(`/api/therapist/clients/${clientId}/company-credit${qs}`);
      if (!res.ok) return null;
      return res.json() as Promise<CompanyCreditInfo | null>;
    },
    enabled: !!clientId,
  });

  // What the client is billed for a non-company manual booking: priced in the
  // business default country, not the therapist's own country (manual-booking.ts).
  const { data: manualQuote = null } = useQuery({
    queryKey: ["sessions", "price-quote", "manual", serviceId],
    queryFn: async () => {
      const res = await fetch(`/api/sessions/price-quote?serviceId=${encodeURIComponent(serviceId)}&mode=manual`);
      if (!res.ok) return null;
      return res.json() as Promise<{ priceCharged: number }>;
    },
    enabled: !!serviceId,
  });

  const services = servicesData?.services ?? [];
  const clients = clientsData?.clients ?? [];
  const selectedService = services.find((s) => s.id === serviceId);
  const companyCharge = selectedService ? (companyCredit?.chargeKm ?? null) : null;
  // Ručni booking ide na teret firme samo kad firma plaća cijelu seansu;
  // sufinansiranje važi kad klijent sam zakaže termin.
  const companyCofunds = !!companyCredit && companyCredit.cofundingPercent < 100;
  const companySufficient =
    !!companyCredit &&
    !companyCofunds &&
    companyCharge != null &&
    (companyCredit.billingModel === "invoice" || companyCredit.balance >= companyCharge);

  // Auto-select company credit on the payment step when it is available and
  // sufficient, unless the therapist has already picked a method manually.
  useEffect(() => {
    if (step === "payment" && companySufficient && !paymentTouched) {
      setPaymentMode("company_credit");
    }
  }, [step, companySufficient, paymentTouched]);

  // Never leave company_credit selected when it is not a valid choice.
  useEffect(() => {
    if (paymentMode === "company_credit" && !companySufficient) {
      setPaymentMode("send_link");
    }
  }, [paymentMode, companySufficient]);

  const paymentOptions: {
    value: PaymentMode;
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string; weight?: "regular" | "fill" | "duotone" }>;
    disabled?: boolean;
  }[] = companyCredit
    ? [
        {
          value: "company_credit",
          title: "Plati preko firme",
          description: companyCofunds
            ? `${companyCredit.companyName} sufinansira ${companyCredit.cofundingPercent} %. Sufinansiranje važi kad klijent sam zakaže termin.`
            : companySufficient
            ? `${companyCredit.companyName} · ${companyCredit.billingModel === "invoice" ? "na mjesečnu fakturu" : "naplata"} ${formatCurrency(companyCharge!)}${companyCredit.discountPercent > 0 ? ` (rabat ${companyCredit.discountPercent}%)` : ""}`
            : companyCharge == null
              ? `${companyCredit.companyName} · iznos naplate firmi nije dostupan za ovu uslugu`
              : `Nedovoljno kredita firme (stanje ${formatCurrency(companyCredit.balance)})`,
          icon: Wallet,
          disabled: !companySufficient,
        },
        ...PAYMENT_OPTIONS,
      ]
    : PAYMENT_OPTIONS;

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const canGoPrev = currentYear > today.getFullYear() || (currentYear === today.getFullYear() && currentMonth > today.getMonth());
  const activeIndex = WIZARD_STEPS.findIndex((s) => s.key === step);
  const selectedClientLabel = selectedClient?.name;
  const selectedClientDetail = selectedClient?.email;
  const effectiveModality =
    selectedService?.modality === "both"
      ? modality
      : selectedService?.modality === "in_person"
        ? "in_person"
        : "online";

  const stepValid: Record<WizardStep, boolean> = {
    client: !!clientId,
    service: !!serviceId,
    appointment: !!date && !!time,
    payment: !!paymentMode,
    confirm: false,
  };

  const canSubmit = !!clientId && !!serviceId && !!date && !!time;
  const canAdvanceStep = step === "confirm" ? canSubmit : stepValid[step];

  const submit = () => {
    if (!canSubmit) return;
    onConfirm({
      clientId,
      serviceId,
      date,
      time,
      paymentMode,
      modality:
        selectedService?.modality === "both"
          ? modality
          : selectedService?.modality === "in_person"
            ? "in_person"
            : "online",
    });
  };

  const prevMonth = () => {
    if (!canGoPrev) return;
    if (currentMonth === 0) setCurrentYear((y) => y - 1);
    setCurrentMonth((m) => (m === 0 ? 11 : m - 1));
    setSelectedDate(null);
    setDate("");
    setTime("");
  };

  const nextMonth = () => {
    if (currentMonth === 11) setCurrentYear((y) => y + 1);
    setCurrentMonth((m) => (m === 11 ? 0 : m + 1));
    setSelectedDate(null);
    setDate("");
    setTime("");
  };

  const goNext = () => {
    if (!canAdvanceStep) return;
    if (step === "confirm") {
      submit();
      return;
    }
    setStep(WIZARD_STEPS[Math.min(activeIndex + 1, WIZARD_STEPS.length - 1)]!.key);
  };

  const goBack = () => {
    if (activeIndex === 0) return;
    setStep(WIZARD_STEPS[Math.max(activeIndex - 1, 0)]!.key);
  };

  const renderClientStep = () => (
    <section className="space-y-4">
      <div>
        <h3 className="text-2xl font-medium text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          Koga dodaješ u termin?
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Možeš zakazati termin samo klijentu s kojim si već imao sesiju.
        </p>
      </div>

      {selectedClient ? (
          <div className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 p-3.5">
            <span className="grid size-10 shrink-0 place-content-center rounded-full bg-primary/15 text-primary">
              <User weight="fill" className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-foreground">{selectedClient.name}</div>
              <div className="truncate text-xs text-muted-foreground">{selectedClient.email}</div>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedClient(null);
                setClientId("");
              }}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-3.5" />
              Promijeni
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Pretraži klijente po imenu ili emailu..."
                value={clientQuery}
                onChange={(e) => setClientQuery(e.target.value)}
                autoFocus
              />
            </div>
            <div className="max-h-64 overflow-y-auto rounded-xl border">
              {clientsFetching ? (
                <div className="flex items-center justify-center gap-2 px-3 py-8 text-sm text-muted-foreground">
                  <CircleNotch className="size-4 animate-spin" />
                  Pretražujem...
                </div>
              ) : clients.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                  {clientQuery.trim()
                    ? "Nema rezultata među tvojim klijentima"
                    : "Nemaš još nijednog klijenta"}
                </div>
              ) : (
                <ul className="divide-y">
                  {clients.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setClientId(c.id);
                          setSelectedClient(c);
                        }}
                        className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/60"
                      >
                        <User className="size-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground">{c.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">{c.email}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
    </section>
  );

  const renderServiceStep = () => (
    <section className="space-y-4">
      <div>
        <h3 className="text-2xl font-medium text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          Koju uslugu zakazuješ?
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">Odaberi terapiju koju želiš dodati u kalendar.</p>
      </div>

      {services.length === 0 ? (
        <p className="rounded-xl border border-dashed py-8 text-center text-sm text-muted-foreground">
          Nema dostupnih usluga.
        </p>
      ) : (
        <div className="grid gap-3">
          {services.map((service) => {
            const active = serviceId === service.id;
            return (
              <button
                key={service.id}
                type="button"
                onClick={() => {
                  setServiceId(service.id);
                  setModality("online");
                  setSelectedDate(null);
                  setDate("");
                  setTime("");
                }}
                className={cn(
                  "group relative flex items-stretch overflow-hidden rounded-2xl border bg-card text-left transition-all",
                  active
                    ? "border-primary shadow-[0_8px_24px_-14px_rgba(35,25,10,0.35)] ring-1 ring-primary"
                    : "border-border/70 hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-[0_10px_28px_-16px_rgba(35,25,10,0.3)]",
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    "w-1 shrink-0 transition-colors",
                    active ? "bg-primary" : "bg-transparent group-hover:bg-primary/40",
                  )}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-3 p-4">
                  <div>
                    <p className="text-[17px] font-semibold leading-snug text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
                      {service.name}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-[11.5px] text-muted-foreground">
                        <Clock className="size-3" />
                        {service.durationMinutes} min
                      </span>
                      <span className="rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-[11.5px] text-muted-foreground">
                        {modalityLabel(service.modality, t)}
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className={cn(
                    "flex shrink-0 flex-col items-end justify-center gap-1.5 border-l px-4 py-4 transition-colors",
                    active ? "border-primary/30 bg-primary/[0.06]" : "border-border/60 group-hover:bg-muted/30",
                  )}
                >
                  <span className="whitespace-nowrap text-xl font-bold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
                    {formatCurrency(service.price)}
                  </span>
                  {active && <span className="text-xs font-semibold text-primary">Odabrano</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );

  const renderAppointmentStep = () => (
    <section className="space-y-4">
      <div>
        <h3 className="text-2xl font-medium text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          Kada je termin?
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">Ručno odaberi datum i vrijeme sesije.</p>
      </div>

      <div className="grid gap-5 sm:grid-cols-[300px_1fr] sm:gap-7">
        <div className="rounded-2xl border p-4">
          <CalendarGrid
            therapistId={therapistId}
            currentMonth={currentMonth}
            currentYear={currentYear}
            today={today}
            selectedDate={selectedDate}
            canGoPrev={canGoPrev}
            onPrevMonth={prevMonth}
            onNextMonth={nextMonth}
            onSelectDate={(d) => {
              setSelectedDate(d);
              setDate(toYmd(d));
              setTime("");
            }}
          />
        </div>
        <div>
          {!selectedDate || !date ? (
            <p className="grid min-h-[180px] place-items-center rounded-2xl border border-dashed text-center text-sm text-muted-foreground/70">
              {t("booking.selectAppointment")}
            </p>
          ) : (
            <CompactTimeSlots
              therapistId={therapistId}
              serviceId={selectedService?.id ?? null}
              dateStr={date}
              selectedTime={time || null}
              enabled={!!selectedService}
              onSelectTime={setTime}
            />
          )}
        </div>
      </div>

      {selectedService?.modality === "both" && (
        <section className="space-y-2">
          <Label>{t("booking.modalityQuestion")}</Label>
          <RadioGroup
            value={modality}
            onValueChange={(v) => setModality(v as "online" | "in_person")}
            className="grid gap-2 sm:grid-cols-2"
          >
            {(["online", "in_person"] as const).map((m) => {
              const id = `${ids}-mod-${m}`;
              return (
                <Label
                  key={m}
                  htmlFor={id}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm font-medium transition-colors",
                    modality === m
                      ? "border-primary/40 bg-primary/5"
                      : "border-border hover:bg-muted/50",
                  )}
                >
                  <RadioGroupItem id={id} value={m} />
                  <span>
                    {m === "online"
                      ? t("booking.modalityOnline")
                      : t("booking.modalityInPerson")}
                  </span>
                </Label>
              );
            })}
          </RadioGroup>
        </section>
      )}
    </section>
  );

  const renderPaymentStep = () => (
    <section className="space-y-4">
      <div>
        <h3 className="text-2xl font-medium text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          Kako se naplaćuje?
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">Odaberi kako se termin naplaćuje.</p>
      </div>

      <RadioGroup
        value={paymentMode}
        onValueChange={(v) => {
          setPaymentTouched(true);
          setPaymentMode(v as PaymentMode);
        }}
        className="gap-2.5"
      >
        {paymentOptions.map((opt) => {
          const Icon = opt.icon;
          const active = paymentMode === opt.value;
          return (
            <div key={opt.value}>
              <label
                htmlFor={`${ids}-pm-${opt.value}`}
                className={cn(
                  "flex items-start gap-3 rounded-xl border p-3.5 transition-colors",
                  opt.disabled
                    ? "cursor-not-allowed border-border/40 bg-muted/30 opacity-60"
                    : active
                      ? "cursor-pointer border-primary bg-primary/5 ring-1 ring-primary/30"
                      : "cursor-pointer border-border hover:bg-muted/50",
                )}
              >
                <RadioGroupItem
                  id={`${ids}-pm-${opt.value}`}
                  value={opt.value}
                  disabled={opt.disabled}
                  className="mt-0.5"
                />
                <Icon
                  weight="duotone"
                  className={cn(
                    "mt-0.5 size-5 shrink-0",
                    active ? "text-primary" : "text-muted-foreground",
                  )}
                />
                <div className="flex-1 space-y-0.5">
                  <div className="text-sm font-medium text-foreground">{opt.title}</div>
                  <p className="text-xs text-muted-foreground">{opt.description}</p>
                </div>
              </label>
            </div>
          );
        })}
      </RadioGroup>
    </section>
  );

  const renderConfirmStep = () => (
    <section className="space-y-4">
      <div>
        <h3 className="text-2xl font-medium text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          Potvrdi termin
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">Pregledaj podatke prije spremanja u kalendar.</p>
      </div>

      <div className="rounded-2xl border bg-card p-4">
        <div className="mb-3 flex items-center gap-2.5">
          <span className="grid size-9 place-items-center rounded-xl bg-primary/10 text-primary">
            <CalendarBlank className="size-5" weight="duotone" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">{selectedService?.name || "Usluga nije odabrana"}</p>
            <p className="text-xs text-muted-foreground">
              {date && time ? `${formatDateLabel(date)} u ${time}` : "Termin nije kompletan"}
            </p>
          </div>
        </div>
        <div>
          <SummaryRow label="Klijent" value={selectedClientLabel} />
          <SummaryRow label="Email" value={selectedClientDetail} />
          <SummaryRow label="Trajanje" value={selectedService ? `${selectedService.durationMinutes} min` : null} />
          <SummaryRow label="Način sesije" value={selectedService ? modalityLabel(effectiveModality, t) : null} />
          <SummaryRow label="Naplata" value={paymentLabel(paymentMode)} />
        </div>
      </div>
    </section>
  );

  const renderStep = () => {
    if (step === "client") return renderClientStep();
    if (step === "service") return renderServiceStep();
    if (step === "appointment") return renderAppointmentStep();
    if (step === "payment") return renderPaymentStep();
    return renderConfirmStep();
  };

  const summaryRows: Array<[string, string | null | undefined]> = [
    ["Klijent", selectedClientLabel],
    ["Usluga", selectedService?.name],
    ["Datum", date ? formatDateLabel(date) : null],
    ["Vrijeme", time],
    ["Način", selectedService ? modalityLabel(effectiveModality, t) : null],
    ["Naplata", paymentLabel(paymentMode)],
  ];

  return (
    <Dialog open onClose={onClose} className="sm:max-w-4xl">
      <DialogHeader onClose={onClose}>
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-0.5">
              <DialogTitle>Dodaj termin</DialogTitle>
              <DialogDescription className="mt-0">Ručno dodaj sesiju u svoj kalendar</DialogDescription>
            </div>
            <span className="hidden rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground sm:inline-flex">
              {activeIndex + 1} / {WIZARD_STEPS.length}
            </span>
          </div>
          <BookingStepper steps={WIZARD_STEPS} activeIndex={activeIndex} />
        </div>
      </DialogHeader>

      <DialogBody className="p-0">
        <div className="grid min-h-[520px] lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="p-5 sm:p-6">
            {renderStep()}
          </div>
          <aside className="border-t bg-white p-5 lg:border-l lg:border-t-0">
            <div className="sticky top-0">
              <div className="mb-3 flex items-center gap-2">
                <Stethoscope className="size-4 text-primary" weight="duotone" />
                <h3 className="text-sm font-semibold text-foreground">Pregled termina</h3>
              </div>
              <div>
                {summaryRows.map(([label, value]) => (
                  <SummaryRow key={label} label={label} value={value} />
                ))}
                {selectedService && (
                  <div className="mt-4 rounded-xl bg-background p-3 text-sm">
                    <span className="text-muted-foreground">Iznos</span>
                    <div className="mt-1 text-xl font-bold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
                      {formatCurrency(
                        paymentMode === "company_credit" && companyCharge != null
                          ? companyCharge
                          : (manualQuote?.priceCharged ?? selectedService.price),
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </DialogBody>

      <DialogFooter className="items-center">
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          Otkaži
        </Button>
        <Button variant="outline" onClick={goBack} disabled={activeIndex === 0 || isPending}>
          <CaretLeft className="size-4" />
          Nazad
        </Button>
        <Button onClick={goNext} disabled={!canAdvanceStep || isPending}>
          {isPending ? (
            <>
              <CircleNotch className="size-4 animate-spin" />
              Spremam…
            </>
          ) : step === "confirm" ? (
            "Sačuvaj"
          ) : (
            <>
              Dalje
              <CaretRight className="size-4" />
            </>
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
